import type { Header } from '@tanstack/vue-table';

/** DataTable column reorder policy: not select/expand, not pinned, `meta.reorderable`. */
export function isDataTableHeaderReorderable<TData>(header: Header<TData, unknown>): boolean {
  const col = header.column;
  if (col.id === 'select' || col.id === 'expand') return false;
  if (col.getIsPinned()) return false;
  return Boolean(col.columnDef.meta?.reorderable);
}

/**
 * Replacing reorderable header positions with `newReorderableIds` (Sortable order) while keeping
 * fixed columns (e.g. select) in place.
 */
export function mergeReorderableIntoVisibleOrder<TData>({
  visibleHeaders,
  newReorderableIds,
  isReorderable,
}: {
  visibleHeaders: Header<TData, unknown>[];
  newReorderableIds: string[];
  isReorderable: (header: Header<TData, unknown>) => boolean;
}): string[] {
  const q = [...newReorderableIds];
  return visibleHeaders.map((h) => {
    if (isReorderable(h)) {
      const id = q.shift();
      if (id == null) throw new Error('Column reorder: reorderable id sequence too short');
      return id;
    }
    return h.column.id;
  });
}

/** TanStack `columnOrder`: visible ids in header order, then hidden leaf ids (unchanged relative tail). */
export function dataTableColumnOrderWithHiddenAppended({
  mergedVisibleColumnIds,
  allLeafColumnIds,
}: {
  mergedVisibleColumnIds: string[];
  allLeafColumnIds: string[];
}): string[] {
  const hiddenIds = allLeafColumnIds.filter((id) => !mergedVisibleColumnIds.includes(id));
  return [...mergedVisibleColumnIds, ...hiddenIds];
}

/**
 * Reads reorderable column ids in DOM order after Sortable updates the `<tr>`. Uses the same markers as
 * DataTable: direct `th` children with `data-stash-column-reorder` and `data-column-id` (Sortable
 * `oldIndex` / `newIndex` are not comparable to the reorderable-id list when non-draggable `th` exist).
 */
export function reorderableColumnIdsFromHeaderRow(headerRow: HTMLElement): string[] {
  const ids: string[] = [];
  for (const child of headerRow.children) {
    if (!(child instanceof HTMLTableCellElement) || child.tagName !== 'TH') continue;
    if (!child.matches('[data-stash-column-reorder]')) continue;
    const id = child.getAttribute('data-column-id');
    if (id != null && id !== '') ids.push(id);
  }
  return ids;
}
